import { observable, makeObservable, reaction, when } from "mobx";
import ActualProgressEntry from "./ActualProgressEntry";
import Distribution from "./Distribution";
import PlannedProgressEntry from "./PlannedProgressEntry";
import {
  ApplyDistributionToTask,
  ApplyDistributionToTaskPreBaseline,
  UpdateTask,
} from "../operations/TaskOperations";
import { UpdateActuals } from "../operations/TaskPackageOperations";
import { updateParentTaskPackage } from "../util/TaskUtil";
import { isNullOrUndefined } from "../util/HelperUtil";
import TaskPackage from "./TaskPackage";
import ForecastedProgressEntry from "./ForecastedProgressEntry";
import { DateTime } from 'luxon';


export default class Task {
  project;
  taskPackage;
  id;
  displayIndex;
  name;
  parent;
  parentDisplayIndex;
  distribution;
  plannedCost;
  plannedDuration;
  plannedStartDate;
  plannedStartPeriod;
  plannedEndDate;
  plannedEndPeriod;
  forecastedEndPeriod;
  actualStartDate = '';
  actualEndDate = '';
  actualProgress;
  plannedProgress;
  forecastedProgress;
  forecastMethod;
  constantForcastValue;
  code;
  oldId;
  isPackage = false;
  analysis = [];
  isNew = false;

  constructor(
    code,
    project,
    taskPackage,
    id,
    displayIndex,
    name,
    parent,
    parentDisplayIndex,
    distribution,
    plannedCost,
    plannedDuration,
    plannedStartDate,
    plannedStartPeriod,
    plannedEndDate,
    plannedEndPeriod,
    forecastedEndPeriod,
    actualProgress,
    plannedProgress,
    forecastedProgress,
    forecastMethod,
    constantForcastValue
  ) {
    this.code = code;
    this.project = project;
    this.taskPackage = taskPackage;
    this.id = id;
    this.displayIndex = displayIndex;
    this.name = name;
    this.parent = parent;
    this.parentDisplayIndex = parentDisplayIndex;
    this.distribution = distribution;
    this.plannedCost = plannedCost;
    this.plannedDuration = plannedDuration;
    this.plannedStartDate = plannedStartDate;
    this.plannedStartPeriod = plannedStartPeriod;
    this.plannedEndDate = plannedEndDate;
    this.plannedEndPeriod = plannedEndPeriod;
    this.forecastedEndPeriod = forecastedEndPeriod;
    this.actualProgress = actualProgress !== null ? actualProgress : [];
    this.plannedProgress = plannedProgress !== null ? plannedProgress : [];
    this.forecastedProgress =
      forecastedProgress !== null ? forecastedProgress : [];
    this.forecastMethod = forecastMethod;
    this.constantForcastValue =
      constantForcastValue !== null ? constantForcastValue : 0;
    when(() => this.plannedCost !== undefined, () => {
      this.recalculateCosts = reaction(() => {
        return this.plannedCost;
      },
        () => {
          this.updateTask();
        },
        { delay: 2000 }
      );
    });

    makeObservable(this, {
      id: observable,
      code: observable,
      name: observable,
      forecastMethod: observable,
      constantForcastValue: observable,
      distribution: observable,
      plannedCost: observable,
      plannedDuration: observable,
      plannedStartDate: observable,
      plannedEndDate: observable,
      plannedStartPeriod: observable,
      plannedEndPeriod: observable,
      actualStartDate: observable,
      actualEndDate: observable,
      actualProgress: observable,
      plannedProgress: observable,
      //forecastedProgress: observable,
      analysis: observable,
    })
  }

  static BasicTask(
    code,
    project,
    id,
    displayIndex,
    name,
    parent,
    parentDisplayIndex,
    plannedStartDate
  ) {
    return new Task(
      code,
      project,
      null,
      id,
      displayIndex,
      name,
      parent,
      parentDisplayIndex,
      new Distribution(project, this, "Uniform", "Uniform"),
      0,
      "0",
      plannedStartDate,
      "",
      new Date().toString(),
      "",
      "",
      [],
      [],
      [],
      "Default",
      0
    );
  }

  applyDistribution(distributionCode) {
    const dist = new Distribution();
    dist.code = distributionCode;
    this.distribution = dist;
    ApplyDistributionToTask(this, distributionCode, (progressPlan) => {
      this.plannedProgress = progressPlan.map((entry) =>
        PlannedProgressEntry.fromJS(this.project, entry)
      );
      this.plannedProgress.forEach((element) => {
        element.task = this;
        let actualProgressEntry = this.actualProgress.find(
          (ap) => ap.period === element.period
        );
        if (actualProgressEntry !== undefined) {
          actualProgressEntry.anticipatedCost = element.expectedCost;
        }
      });
    });
  }

  applyDistributionPreBaseline(distributionCode) {
    const dist = new Distribution();
    dist.code = distributionCode;
    this.distribution = dist;
    ApplyDistributionToTaskPreBaseline(this, distributionCode, (progressPlan) => {
      this.plannedProgress = progressPlan.map((entry) =>
        PlannedProgressEntry.fromJS(this.project, entry)
      );
      this.plannedProgress.forEach((element) => {
        element.task = this;
        let actualProgressEntry = this.actualProgress.find(
          (ap) => ap.period === element.period
        );
        if (actualProgressEntry !== undefined) {
          actualProgressEntry.anticipatedCost = element.expectedCost;
        }
      });
    });
  }

  // Sends task package to server side and updates the duration and periods
  async updateTask() {
    await UpdateTask(this.project, this, (t) => {
      let task = Task.fromJS(this.project, t, t.parent);

      this.plannedDuration = task.plannedDuration;
      this.plannedStartPeriod = Math.min(
        ...task.plannedProgress.map(
          (plannedProgressEntry) => plannedProgressEntry.period
        )
      );
      this.plannedEndPeriod = Math.max(
        ...task.plannedProgress.map(
          (plannedProgressEntry) => plannedProgressEntry.period
        )
      );
      this.plannedProgress = task.plannedProgress;
      this.actualProgress = task.actualProgress;

      updateParentTaskPackage(this);
    });
  }

  updateParentTaskPackageActuals() {
    let parent = this.getParentTaskPackage();
    debugger;
    if (!isNullOrUndefined(parent)) {
      UpdateActuals(this.project, parent, (tp) => {
        let taskPackage = TaskPackage.fromJS(this.project, tp, tp.parent);
        parent.actualProgress = taskPackage.actualProgress;
      });
      parent.updateParentTaskPackageActuals();
    }
  }

  getActualProgressForPeriod(period) {
    if (this.actualProgress != null) {
      const existingPeriods = this.actualProgress.map(e => e.period);
      // Determine the current maximum period (or 0 if none exist)
      const maxActualPeriod = existingPeriods.length > 0 && existingPeriods.find(e => e > 0) ? Math.max(...existingPeriods) : 0;

      // If the requested period creates a gap, fill it (e.g., max is 92, request is 100, fill 93-99)
      if (period > maxActualPeriod + 1) {
        for (let i = maxActualPeriod + 1; i < period; i++) {
          this.getActualProgressForPeriod(i); // Recursively call to ensure all intermediate Planned and Actual entries are created
        }
      }

      // Find or create the requested period
      const filteredActual = this.actualProgress.filter(
        (entry) => entry.period == period //== because it does not prove anything to be fussy if it's provided as number or string
      );
      let result = filteredActual[0];
      if (result == null) {
        result = new ActualProgressEntry(
        this.project,
        this,
        period,
        0,
        0,
        0,
        null
      );
        //this.actualProgress.push(result);
        //this.getPlannedProgressForPeriod(period); // Ensure that planned progress entry also exists
      }
      return result;
    }
  }

  getPlannedProgressForPeriod(period) {
    if (this.plannedProgress != null) {
      const existingPeriods = this.plannedProgress.map(e => e.period);
      // Determine the current maximum period (or 0 if none exist)
      const maxPlannedPeriod = existingPeriods.length > 0 && existingPeriods.find(e => e > 0) ? Math.max(...existingPeriods) : 0;

      // If the requested period creates a gap, fill it (e.g., max is 92, request is 100, fill 93-99)
      if (period > maxPlannedPeriod + 1) {
        for (let i = maxPlannedPeriod + 1; i < period; i++) {
          this.getPlannedProgressForPeriod(i); // Recursively call to ensure all intermediate Planned and Actual entries are created
        }
      }

      // Find or create the requested period
      const filteredPlan = this.plannedProgress.filter(
        (entry) => entry.period == period //== because it does not prove anything to be fussy if it's provided as number or string
      );
      let result = filteredPlan[0];
      if (result == null) {
        result = new PlannedProgressEntry(
        this.project,
        this,
        period,
        0,
        0,
        0,
        false
      );
        //this.plannedProgress.push(result);
        //this.getActualProgressForPeriod(period); // Ensure that actual progress entry also exists
      }
      return result;
    }
  }

  getForecastedProgressForPeriod(period) {
    if (this.forecastedProgress != null) {
      const filteredforecast = this.forecastedProgress.filter(
        (entry) => entry.period == period
      );
      let result = filteredforecast[0];
      if (result == null) {
        result = new ForecastedProgressEntry(
          this.project,
          this,
          period,
          0,
          0
        );
        this.forecastedProgress.push(result);
      }
      return result;
    }
  }

  getParentTaskPackage() {
    if (this.id === 0) {
      return null;
    }
    if (this.parent === 0) {
      return this.project.rootTaskPackage;
    }
    return this.project.rootTaskPackage
      .depthFirstTraversal()
      .find((tp) => tp.id === this.parent);
  }

  getRealForecastMethod() {
    if (this.forecastMethod && this.forecastMethod !== "Default") {
      return this.forecastMethod;
    } else {
      return this.getParentTaskPackage().getRealForecastMethod()
    }
  }

  // refreshDisposer = reaction(() => {
  //   return this.plannedStartDate;
  // },
  // (data, reaction) => {
  //   reaction.dispose()
  //   refreshProject(this.project, (result) => {
  //     this.project.rootTaskPackage = TaskPackage.fromJS(this.project, result.rootTaskPackage);
  //   })
  // },
  // { delay : 750}
  // );

  toJS() {
    return {
      id: this.id,
      name: this.name,
      parent: this.parent,
      displayIndex: this.displayIndex,
      parentDisplayIndex: this.parentDisplayIndex,
      forecastMethod: this.forecastMethod,
      constantForcastValue: this.constantForcastValue,
      distribution: this.distribution ? this.distribution.toJS() : null,
      plannedCost: !isNullOrUndefined(this.plannedCost)
        ? this.plannedCost
        : null,
      plannedDuration: this.plannedDuration,
      plannedStartDate: DateTime.fromISO(this.plannedStartDate).isValid ? this.plannedStartDate : "",
      plannedEndDate: DateTime.fromISO(this.plannedEndDate).isValid ? this.plannedEndDate : "",
      actualStartDate: DateTime.fromISO(this.actualStartDate).isValid ? this.actualStartDate : "",
      actualEndDate: DateTime.fromISO(this.actualEndDate).isValid ? this.actualEndDate : "",
      actualProgress: this.actualProgress.map((entry) => entry.toJS()),
      plannedProgress: this.plannedProgress.map((entry) => entry.toJS()),
      forecastedProgress: this.forecastedProgress.map((entry) => entry.toJS()),
      forecastedEndPeriod: this.forecastedEndPeriod,
      code: this.code,
      oldId: this.oldId,
    };
  }

  static fromJS(store, object, parent) {
    const task = new Task();
    if (object != null) {
      task.name = object.name;
      task.id = object.id;
      task.code = object.code;
      task.parent = parent;
      task.displayIndex = object.displayIndex;
      task.parentDisplayIndex = object.parentDisplayIndex;
      task.forecastMethod = object.forecastMethod;
      task.constantForcastValue = object.constantForcastValue;
      task.plannedCost = object.plannedCost;
      task.plannedDuration = object.plannedDuration;
      task.plannedStartDate = object.plannedStartDate;
      task.plannedEndDate = object.plannedEndDate;
      task.actualStartDate = !!object.actualStartDate ? object.actualStartDate : object.plannedStartDate;
      task.actualEndDate = !! object.actualEndDate  ? object.actualEndDate : object.plannedEndDate;;
      task.project = store;
      task.forecastedEndPeriod = object.forecastedEndPeriod;

      if (isNullOrUndefined(object.actualProgress)) {
        task.actualProgress = [];
      } else {
        task.actualProgress = object.actualProgress.map((entry) =>
          ActualProgressEntry.fromJS(store, entry)
        );
        task.actualProgress.forEach((element) => {
          element.task = task;
        });
      }

      if (isNullOrUndefined(object.plannedProgress)) {
        task.plannedProgress = [];
      } else {
        task.plannedProgress = object.plannedProgress.map((entry) =>
          PlannedProgressEntry.fromJS(store, entry)
        );
        task.plannedProgress.forEach((element) => {
          element.task = task;
        });
      }

      if (isNullOrUndefined(object.forecastedProgress)) {
        task.forecastedProgress = [];
      } else {
        task.forecastedProgress = object.forecastedProgress.map((entry) =>
          ForecastedProgressEntry.fromJS(store, entry)
        );
        task.forecastedProgress.forEach((element) => {
          element.task = task;
        });
      }

      task.distribution = Distribution.fromJS(object.distribution);
      task.oldId = object.oldId;

      if (task.plannedProgress !== undefined && task.plannedProgress !== null) {
        task.plannedStartPeriod = Math.min(
          ...task.plannedProgress.map((entry) => entry.period)
        );
        task.plannedEndPeriod = Math.max(
          ...task.plannedProgress.map((entry) => entry.period)
        );
      } else {
        task.plannedStartPeriod = 0;
        task.plannedEndPeriod = 0;
      }
    }
    return task;
  }
}


